import React from 'react';
import { IAuditoriaAssetRecoveryResultado } from '../interfaces/IAuditoriaAssetRecovery';

interface Props {
  resultado: IAuditoriaAssetRecoveryResultado;
  onVerDetalhe: () => void;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value?: number) =>
  `${(value || 0).toFixed(4)}%`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
                          'badge-light-info';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  if (tipo === 'OMISSAO_RECEITA')
    return <span className='badge badge-light-danger'>Omissão de Receita</span>;
  if (tipo === 'SEM_CONTRAPARTIDA')
    return <span className='badge badge-light-warning'>Sem Contrapartida</span>;
  return <span className='badge badge-light-info'>Reversão Parcial</span>;
};

const AuditoriaResumoAssetRecovery: React.FC<Props> = ({ resultado, onVerDetalhe }) => {
  if (!resultado || resultado.itens.length === 0) return null;

  const top5 = [...resultado.itens]
    .sort((a, b) => b.divergencia - a.divergencia)
    .slice(0, 5);

  const statusCls =
    resultado.statusGeral === 'ERRO'   ? 'border-danger' :
    resultado.statusGeral === 'ALERTA' ? 'border-warning' :
    resultado.statusGeral === 'AVISO'  ? 'border-info' :
                                         'border-success';

  return (
    <div className={`card card-custom card-stretch mb-5 border-top border-4 ${statusCls}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-5 mb-1'>
            Auditoria de Reversão de Perdas — Asset Recovery
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.totalErros} erro(s) · {resultado.totalAlertas} alerta(s) · {resultado.totalAvisos} aviso(s)
            {resultado.passivoTotal > 0 && (
              <> · Passivo Presumido: <strong className='text-danger'>{formatCurrency(resultado.passivoTotal)}</strong></>
            )}
          </span>
        </h3>
        <div className='card-toolbar'>
          <button className='btn btn-sm btn-light-primary' onClick={onVerDetalhe}>
            Ver Detalhes
          </button>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='row g-3 mb-4'>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Erros — Omissão {'>'} 0,5% RB</div>
              <div className='fw-bolder fs-3 text-danger'>{resultado.totalErros}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Alertas — Sem Contrapartida</div>
              <div className='fw-bolder fs-3 text-warning'>{resultado.totalAlertas}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Avisos — Reversão Parcial</div>
              <div className='fw-bolder fs-3 text-info'>{resultado.totalAvisos}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Passivo Presumido (ERRO+ALERTA)</div>
              <div className='fw-bolder fs-5 text-danger'>{formatCurrency(resultado.passivoTotal)}</div>
            </div>
          </div>
        </div>

        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
            <thead>
              <tr className='fw-bolder text-muted'>
                <th className='text-center'>Mês/Ano</th>
                <th className='text-end'>Créd. Baixa Estoques</th>
                <th className='text-end'>Créd. PDD</th>
                <th className='text-end'>Total Crédito Despesa</th>
                <th className='text-end'>Rec. Reversão</th>
                <th className='text-end'>Divergência</th>
                <th className='text-center'>Ocorrência</th>
                <th className='text-center'>Status</th>
              </tr>
            </thead>
            <tbody>
              {top5.map((item, i) => (
                <tr key={i}>
                  <td className='text-center fw-bold'>{item.mesAno}</td>
                  <td className='text-end'>{formatCurrency(item.creditoBaixaEstoques)}</td>
                  <td className='text-end'>{formatCurrency(item.creditoPDD)}</td>
                  <td className='text-end fw-bold'>{formatCurrency(item.creditoDespesaTotal)}</td>
                  <td className='text-end'>{formatCurrency(item.receitaReversao)}</td>
                  <td className='text-end fw-bolder text-danger'>{formatCurrency(item.divergencia)}</td>
                  <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
                  <td className='text-center'><StatusBadge status={item.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {resultado.itens.length > 5 && (
          <div className='text-center mt-2'>
            <button className='btn btn-sm btn-light' onClick={onVerDetalhe}>
              Ver todas as {resultado.itens.length} ocorrências
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaResumoAssetRecovery;
